/**
 * @file 非同期処理のフローを管理するためのライブラリです。
 * 複数のタスクをシーケンシャル、ブランチ、リトライなどの制御構造で組み合わせることができます。
 *
 * 使いどころ:
 * - 画面初期化 → ユーザー操作待ち → API呼び出し → モーダル表示…のような「段階的な処理」を
 *   1つの読みやすいフローとして記述したい時
 *
 * 基本ルール:
 * - 各タスク（FlowTask）は `flowProcess(done)` 内で最後に `done(true|false)` を呼び出します。
 * - `done(false)` が返ると、そのコンテナ（SequentialFlow 等）は失敗扱いとなり、後続を止めます。
 * - `safe()` は失敗したらシーケンス全体を最初からやり直します。
 *
 * @example
 * // 最小例（順次実行）
 * // import { createFlow, FlowTask } from "@/controllers/flow";
 *
 * class InitializeTask extends FlowTask {
 *   flowProcess(done) {
 *     // 初期化処理…
 *     done();
 *   }
 * }
 *
 * class WaitClickTask extends FlowTask {
 *   flowProcess(done) {
 *     document.querySelector("#play-button")?.addEventListener("click", () => done(), { once: true });
 *   }
 * }
 *
 * createFlow().flow(InitializeTask).flow(WaitClickTask).run();
 *
 * @example
 * // 分岐例（URLや条件で処理を切り替える）
 * // import { createFlow, branches, skip, FlowTask } from "@/controllers/flow";
 *
 * const BRANCH_LOGIN = "login";
 * const BRANCH_NOOP  = "noop";
 *
 * class OpenLoginModalTask extends FlowTask {
 *   flowProcess(done) {
 *     // モーダル表示→結果に応じて done(true/false)
 *     done(true);
 *   }
 * }
 *
 * const selector = () => (window.__needLogin ? BRANCH_LOGIN : BRANCH_NOOP);
 *
 * createFlow()
 *   .flow(branches(selector)
 *     .branch(BRANCH_LOGIN, OpenLoginModalTask)
 *     .branch(BRANCH_NOOP,  skip()))
 *   .run();
 */

/**
 * デバッグモードの状態を管理するシングルトンクラス。
 */
class FlowDebugger {
  constructor() {
    if (FlowDebugger._instance) {
      return FlowDebugger._instance;
    }
    this._isDebug = false;
    FlowDebugger._instance = this;
  }

  static get instance() {
    if (!this._instance) {
      this._instance = new FlowDebugger();
    }
    return this._instance;
  }

  /**
   * デバッグモードを設定します。
   * @param {boolean} [enabled=true]
   */
  setDebug(enabled = true) {
    this._isDebug = enabled;
  }

  /**
   * 現在のデバッグモードの状態を取得します。
   * @returns {boolean}
   */
  get isDebug() {
    return this._isDebug;
  }
}

// モジュール内で使用するシングルトンインスタンスを作成
const flowDebugger = FlowDebugger.instance;

/**
 * 無効なフローが指定された場合にスローされるエラー。
 */
class InvalidFlowError extends Error {
  constructor(message) {
    super(message);
    this.name = "InvalidFlowError";
  }
}

/**
 * @classdesc すべてのフローコンポーネントの基底クラス。
 * @abstract
 */
export class FlowBase {
  /**
   * デバッグモードが有効な場合にコンソールにメッセージを記録します。
   * @param {string} message 記録するメッセージ。
   */
  log(message) {
    if (flowDebugger.isDebug) {
      console.log(message);
    }
  }

  /**
   * デバッグモードが有効な場合にコンソールに警告を記録します。
   * @param {string} message 記録する警告メッセージ。
   */
  warn(message) {
    if (flowDebugger.isDebug) {
      console.warn(message);
    }
  }

  /**
   * フローを実行する内部メソッド。サブクラスでオーバーライドされます。
   * @protected
   * @returns {Promise<boolean>} フローの実行が成功した場合は true、失敗した場合は false を返します。
   */
  async _run() {
    return true;
  }
}

/**
 * @classdesc 単一の非同期タスクを表すフローコンポーネント。
 * 初期化、メイン処理、後処理のライフサイクルを持ちます。
 *
 * 実装の要点:
 * - `flowProcess(done)` の中で、成功時 `done(true)` / 失敗時 `done(false)` を呼び出します。
 * - `flowProcess` は async でも構いませんが、最終的に必ず `done(...)` を呼ぶ必要があります。
 *
 * @example
 * // API呼び出しを含むタスク例
 * // class FetchTask extends FlowTask {
 * //   async flowProcess(done) {
 * //     try {
 * //       const res = await fetch("/path");
 * //       done(res.ok);
 * //     } catch (_e) {
 * //       done(false);
 * //     }
 * //   }
 * // }
 */
export class FlowTask extends FlowBase {
  /**
   * このフローを実行すべきかどうかを判断します。
   * @returns {boolean} 実行する場合は true、スキップする場合は false。
   */
  shouldFlow() {
    return true;
  }

  /**
   * フローの実行を管理します。
   * shouldFlow, initialize, flowProcess, deinitialize の順でメソッドを呼び出します。
   * @protected
   * @returns {Promise<boolean>} タスクの実行が成功した場合は true、失敗した場合は false を返します。
   */
  async _run() {
    this.log(`[START] ${this.constructor.name}`);

    if (!this.shouldFlow()) {
      this.log(`[SKIP] ${this.constructor.name}`);
      this.log(`[END] ${this.constructor.name} - Success: true`);
      return true; // スキップした場合もフロー全体としては成功とみなす
    }

    let success = true;
    this.initialize();

    try {
      const flowPromise = new Promise((resolve) => {
        const done = (success = true) => resolve(success);
        this.flowProcess(done);
      });
      success = await flowPromise;
    } catch (error) {
      console.error(`[${this.constructor.name}] An error occurred in flowProcess.`, error);
      success = false;
    } finally {
      this.deinitialize();
    }

    this.log(`[END] ${this.constructor.name} - Success: ${success}`);
    return success;
  }

  /**
   * フローのメイン処理の前に呼び出される初期化処理。
   * 必要に応じてサブクラスでオーバーライドします。
   */
  initialize() { }

  /**
   * フローの後処理として呼び出される処理。
   * 処理の成功・失敗にかかわらず、initializeが呼ばれた後には必ず呼ばれます。
   * 必要に応じてサブクラスでオーバーライドします。
   */
  deinitialize() { }

  /**
   * メインとなる非同期処理を実装します。
   * このメソッドはサブクラスで必ずオーバーライドする必要があります。
   * @returns {Promise<boolean>} 処理が成功した場合は true、失敗した場合は false を返します。
   * @abstract
   */
  async flowProcess(done) {
    console.error(`[${this.constructor.name}] flowProcess must be implemented.`);
    done(false); // 未実装の場合は失敗とする
  }
}

/**
 * @classdesc 何も実行せずに即座に成功を返すタスク。
 * 条件分岐などで意図的に処理をスキップしたい場合に使用します。
 */
class SkipFlow extends FlowTask {
  /**
   * 何もせずに常に true を返します。
   * @returns {Promise<boolean>} 常に true。
   */
  async flowProcess(done) {
    done();
  }
}

/**
 * @classdesc 複数のフローを保持し、flowメソッドを提供する共通基底クラス。
 * @abstract
 */
class FlowContainer extends FlowBase {
  constructor() {
    super();
    /**
     * @protected
     * @type {FlowBase[]}
     */
    this.flows = [];
  }

  /**
   * 新しいフローをコンテナに追加します。
   * @param {typeof FlowBase | FlowBase} newFlow - 追加するフローのクラスまたはインスタンス。
   * @param {*} [args] - newFlowがクラスの場合にコンストラクタに渡す引数。
   * @returns {this} メソッドチェーンを可能にするために自身を返します。
   * @throws {InvalidFlowError} FlowBaseから派生していないものが渡された場合にスローされます。
   */
  flow(newFlow, args = undefined) {
    let instance;
    if (typeof newFlow === "function" && newFlow.prototype instanceof FlowBase) {
      instance = args !== undefined ? new newFlow(args) : new newFlow();
    } else if (newFlow instanceof FlowBase) {
      instance = newFlow;
    } else {
      throw new InvalidFlowError("The provided flow must be a class or an instance derived from FlowBase.");
    }
    this.flows.push(instance);
    return this;
  }
}

/**
 * @classdesc 条件に応じて実行するフローを切り替えるコンポーネント。
 *
 * 注意:
 * - selectorFunc が返したキーに対応する branch が存在しない場合は失敗（false）になります。
 *   そのため「何もしない分岐」を作りたい場合は `skip()` を branch に登録してください。
 */
class FlowBranch extends FlowBase {
  /**
   * @param {function(): string} selectorFunc - 実行するフローのキーを返す関数。
   */
  constructor(selectorFunc) {
    super();
    /**
     * @private
     * @type {function(): string}
     */
    this.selectorFunc = selectorFunc;
    /**
     * @private
     * @type {Object.<string, FlowBase>}
     */
    this.branches = {};
  }

  /**
   * 分岐先のフローを登録します。
   * @param {string} name - フローを識別するためのキー。selectorFuncが返す値と対応します。
   * @param {typeof FlowBase | FlowBase} newFlow - 登録するフローのクラスまたはインスタンス。
   * @param {*} [args] - newFlowがクラスの場合にコンストラクタに渡す引数。
   * @returns {this} メソッドチェーンを可能にするために自身を返します。
   * @throws {InvalidFlowError} FlowBaseから派生していないものが渡された場合にスローされます。
   */
  branch(name, newFlow, args = undefined) {
    let instance;
    if (typeof newFlow === "function" && newFlow.prototype instanceof FlowBase) {
      instance = args !== undefined ? new newFlow(args) : new newFlow();
    } else if (newFlow instanceof FlowBase) {
      instance = newFlow;
    } else {
      throw new InvalidFlowError("The provided flow must be a class or an instance derived from FlowBase.");
    }
    this.branches[name] = instance;
    return this;
  }

  /**
   * selectorFuncの結果に基づいて選択されたフローを実行します。
   * @protected
   * @returns {Promise<boolean>} 選択されたフローの実行結果。該当するフローがない場合は true を返します。
   */
  async _run() {
    this.log(`[START] ${this.constructor.name}`);

    const selectedKey = this.selectorFunc();
    const selectedFlow = this.branches[selectedKey];
    let result;

    if (selectedFlow) {
      this.log(`[FlowBranch] Key "${selectedKey}" selected. Running sub-flow.`);
      result = await selectedFlow._run();
    } else {
      this.warn(`[FlowBranch] Branch with key "${selectedKey}" not found. Canceled.`);
      result = false;
    }

    this.log(`[END] ${this.constructor.name} - Success: ${result}`);
    return result;
  }
}

/**
 * @classdesc 登録されたフローを順次実行するコンポーネント。
 * いずれかのフローが失敗した場合、後続のフローは実行されずに全体が失敗となります。
 *
 * @example
 * // import { sequence, FlowTask } from "@/controllers/flow";
 * // const flow = sequence().flow(TaskA).flow(TaskB);
 */
class SequentialFlow extends FlowContainer {
  /**
   * 登録されたフローを順番に実行します。
   * @protected
   * @returns {Promise<boolean>} すべてのフローが成功した場合は true、いずれかが失敗した場合は false を返します。
   */
  async _run() {
    this.log(`[START] ${this.constructor.name}`);

    let finalResult = true;
    for (const flow of this.flows) {
      const result = await flow._run();
      if (result === false) {
        finalResult = false;
        break;
      }
    }

    this.log(`[END] ${this.constructor.name} - Success: ${finalResult}`);
    return finalResult;
  }
}

/**
 * @classdesc 登録されたフローを順次実行するコンポーネント。
 * いずれかのフローが失敗した場合、最初からすべてのフローを再実行します。
 *
 * @example
 * // 例: 読み込みが失敗したら最初からリトライしたい
 * // import { safe, FlowTask } from "@/controllers/flow";
 * // safe().flow(PreloadTask).flow(ShowModalTask).flow(ConfirmTask)
 */
class SafeFlow extends FlowContainer {
  /**
   * すべてのフローが成功するまで、シーケンス全体を再試行します。
   * @protected
   * @returns {Promise<boolean>} 最終的にすべてのフローが成功した場合に true を返します。
   */
  async _run() {
    this.log(`[START] ${this.constructor.name}`);

    restart: while (true) {
      this.log("[SafeFlow] Starting/Restarting sequence...");
      for (const flow of this.flows) {
        const result = await flow._run();
        if (result === false) {
          this.warn("[SafeFlow] A sub-flow failed. Restarting entire sequence.");
          // 失敗した場合は外側の while ループに戻って最初から再試行
          continue restart;
        }
      }
      // すべてのフローが成功した場合、ループを抜ける
      break;
    }

    this.log(`[END] ${this.constructor.name} - Success: true`);
    return true;
  }
}


/**
 * @classdesc フロー全体のエントリーポイントとなるマネージャ。
 *
 * @example
 * // import { createFlow } from "@/controllers/flow";
 * // createFlow().debug().flow(TaskA).flow(TaskB).run();
 */
export class FlowManager extends FlowContainer {
  /**
   * デバッグモードを設定します。
   * @param {boolean} [enabled=true] デバッグモードを有効にする場合は true。
   * @returns {this} メソッドチェーンを可能にするために自身を返します。
   */
  debug(enabled = true) {
    flowDebugger.setDebug(enabled);
    return this;
  }

  /**
   * 登録されたすべてのフローを実行します。
   * @returns {Promise<void>}
   */
  async run() {
    this.log("[FlowManager] ==> Starting flow execution...");

    for (const flow of this.flows) {
      const result = await flow._run();
      // いずれかのトップレベルフローが失敗したら、そこで全体の実行を停止する
      if (result === false) {
        this.warn("[FlowManager] A top-level flow failed. Halting execution.");
        return;
      }
    }

    this.log("[FlowManager] All flows completed successfully.");
  }
}


// --- ファクトリ関数 ---

/**
 * FlowManagerのインスタンスを生成します。
 * @returns {FlowManager} 新しいFlowManagerインスタンス。
 *
 * @example
 * // import { createFlow } from "@/controllers/flow";
 * // createFlow().flow(TaskA).flow(TaskB).run();
 */
export const createFlow = () => new FlowManager();

/**
 * FlowBranchのインスタンスを生成します。
 * @param {function(): string} selectorFunc - 実行するフローのキーを返す関数。
 * @returns {FlowBranch} 新しいFlowBranchインスタンス。
 *
 * @example
 * // import { branches, skip } from "@/controllers/flow";
 * // const selector = () => (Math.random() > 0.5 ? "A" : "B");
 * // branches(selector).branch("A", TaskA).branch("B", skip());
 */
export const branches = (selectorFunc) => new FlowBranch(selectorFunc);

/**
 * SequentialFlowのインスタンスを生成します。
 * @returns {SequentialFlow} 新しいSequentialFlowインスタンス。
 *
 * @example
 * // import { sequence } from "@/controllers/flow";
 * // sequence().flow(TaskA).flow(TaskB);
 */
export const sequence = () => new SequentialFlow();

/**
 * SafeFlowのインスタンスを生成します。
 * @returns {SafeFlow} 新しいSafeFlowインスタンス。
 *
 * @example
 * // import { safe } from "@/controllers/flow";
 * // safe().flow(TaskA).flow(TaskB);
 */
export const safe = () => new SafeFlow();

/**
 * SkipFlowのインスタンスを生成します。
 * @returns {SkipFlow} 新しいSkipFlowインスタンス。
 *
 * @example
 * // import { branches, skip } from "@/controllers/flow";
 * // branches(selector).branch("noop", skip());
 */
export const skip = () => new SkipFlow();
